/**
 * PATH: packages/job-hub/src/jobs/qa/QaChecklistRow.tsx
 * FILE_ID: JOBHUB-QA-ROW-001
 * PURPOSE: One row of the QA build table — number + indented title, Product cell,
 *          requirement / acceptance clay cells, hold-point toggle and row
 *          (sibling) move/delete controls. Sub-rows render indented by depth.
 * WHY:     Operator: "allow me to add sub rows … where is the product". Split out
 *          of QaChecklistTable so the orchestrator stays under the line limit.
 * RELATED:
 * - ./QaChecklistTable.tsx (maps the row tree to these)
 * - ./EditableCell.tsx (the clay cells)
 */
import { CornerDownRight, Loader2, Trash2 } from 'lucide-react'
import { QaDragHandle } from './QaDragHandle'
import { cn } from '@quotech/tenant-shared/lib/utils'
import { useQaHoldTimer } from './qaHoldTimer'
import { EditableCell } from './EditableCell'
import { joinProductNames, resolvedProductNames } from './qaProductResolve'
import type { QaItemEdit, QaItemInstance } from './qaApi'
import type { QaRowNode } from './checklistTable'

const CELL = 'px-1 py-1 border-r border-border/50 align-top'

export function QaChecklistRow({
  node,
  disabled,
  rowBusy,
  onEditCell,
  onContextMenu,
  selected,
  onToggleSelected,
  areaNames = [],
  checklistNames = [],
  onQuickDelete,
  onMoveUp: _onMoveUp,
  onMoveDown: _onMoveDown,
}: {
  node: QaRowNode<QaItemInstance>
  disabled: boolean
  rowBusy: boolean
  onEditCell: (item: QaItemInstance, fields: QaItemEdit) => void
  onContextMenu: (e: React.MouseEvent | { clientX: number; clientY: number }) => void
  selected: boolean
  onToggleSelected: () => void
  areaNames?: string[]
  checklistNames?: string[]
  onQuickDelete?: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
}) {
  const { item, depth } = node
  const productDisplay = joinProductNames(resolvedProductNames({
    itemNames: item.product_names,
    itemProduct: item.product,
    checklistNames,
    areaNames,
  }))
  const cellDisabled = disabled
  const isSub = depth > 0
  const hold = useQaHoldTimer((point) => onContextMenu(point))

  return (
    <tr
      data-qa-item-row={item.id}
      data-qa-drop={`row:${item.id}`}
      className={cn('group border-b border-border/60 align-top hover:bg-accent/30 data-[qa-drop-active=true]:border-t-2 data-[qa-drop-active=true]:border-primary', isSub && 'bg-muted/20')}
      title="Right-click or long-press for row actions"
      {...hold}
      onContextMenu={(e) => {
        e.preventDefault()
        onContextMenu(e)
      }}
    >
      <td className="px-1 py-1 border-r border-border/50 text-center text-[11px] tabular-nums text-muted-foreground">
        <div className="flex items-center justify-center gap-1 pt-1.5">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelected}
            aria-label={`Select item ${item.item_number}`}
            data-testid={`qa-item-select-${item.id}`}
            className="h-3.5 w-3.5 shrink-0 rounded border-border"
          />
          <QaDragHandle kind="row" id={item.id} label={item.title || 'step'} disabled={disabled} />
          {rowBusy && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
          <span>{item.display_number ?? item.item_number}</span>
        </div>
      </td>
      <td className={CELL}>
        <div className="flex items-start" style={{ paddingLeft: depth * 18 }}>
          {isSub && (
            <CornerDownRight
              className="mt-2 mr-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/50"
              aria-hidden
            />
          )}
          <div className="min-w-0 flex-1">
            <EditableCell
              value={item.title}
              singleLine
              allowEmpty={false}
              disabled={cellDisabled}
              ariaLabel={`Item ${item.item_number} title`}
              placeholder={isSub ? 'Sub-check name' : 'Check name'}
              onCommit={(title) => onEditCell(item, { title })}
            />
            {item.assigned_to_name ? (
              <span className="block truncate text-[11px] text-muted-foreground">
                {item.assigned_to_name}{item.due_date ? ` · ${item.due_date}` : ''}
              </span>
            ) : null}
          </div>
        </div>
      </td>
      <td className={CELL}>
        <EditableCell
          value={productDisplay}
          singleLine
          disabled={cellDisabled}
          ariaLabel={`Item ${item.item_number} product`}
          placeholder="Product / material"
          onCommit={(product) => {
            const ownNames = item.product_names ?? []
            const ownProduct = (item.product ?? '').trim()
            if (ownNames.length === 0 && !ownProduct && product === productDisplay) return
            onEditCell(item, { product })
          }}
        />
      </td>
      <td className={CELL}>
        <EditableCell
          value={item.requirement_text ?? ''}
          disabled={cellDisabled}
          ariaLabel={`Item ${item.item_number} requirement`}
          placeholder="What to verify"
          onCommit={(requirement_text) => onEditCell(item, { requirement_text })}
        />
      </td>
      <td className={CELL}>
        <EditableCell
          value={item.acceptance_criteria ?? ''}
          disabled={cellDisabled}
          ariaLabel={`Item ${item.item_number} acceptance criteria`}
          placeholder="Pass condition"
          onCommit={(acceptance_criteria) => onEditCell(item, { acceptance_criteria })}
        />
      </td>
      <td className="relative px-2 py-1 border-r border-border/50 text-center">
        <input
          type="checkbox"
          checked={!!item.has_hold_point}
          disabled={disabled}
          aria-label={`Park item ${item.item_number}`}
          title="Hold point: shows as (Hold Point) in the ITP with a sign-off"
          onChange={(e) => onEditCell(item, { has_hold_point: e.target.checked })}
          className="mt-2 h-4 w-4 rounded border-border text-amber-600 focus:ring-amber-500"
        />
        {onQuickDelete ? (
          <button
            type="button"
            className="absolute right-0.5 top-0.5 z-10 inline-flex h-5 w-5 items-center justify-center rounded-md text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
            aria-label="Delete row"
            data-testid="qa-row-hover-delete"
            disabled={rowBusy}
            onPointerDown={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onQuickDelete()
            }}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </td>
    </tr>
  )
}

/** A header cell with a drag handle on its right edge to resize the column. */
export function ResizableHeader({
  label,
  onResize,
  disabled = false,
}: {
  label: string
  onResize: (e: React.MouseEvent) => void
  disabled?: boolean
}) {
  return (
    <th className="relative border-r border-border/50 px-2 py-2">
      <span>{label}</span>
      {!disabled && (
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${label} column`}
          onMouseDown={onResize}
          onClick={(e) => e.stopPropagation()}
          className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none hover:bg-primary/40"
        />
      )}
    </th>
  )
}

export default QaChecklistRow
